import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { AuthService } from '../../../services/auth.service';
import { Router } from '@angular/router';
import { TrafficFinesService, TrafficFineListQuery } from '../../../services/traffic-fines.service';
import { AccountsReceivableService } from '../../../services/accounts-receivable.service';
import { ErrorService } from '../../../shared/services/error.service';
import { TrafficFineAccountsPayableSyncService } from '../../../services/traffic-fine-accounts-payable-sync.service';
import {
  TrafficFineDriverIndicationDocumentService,
  TrafficFineDriverIndicationForm
} from '../../../services/traffic-fine-driver-indication-document.service';
import {
  TrafficFineDriverIndication,
  TrafficFineDriverIndicationService
} from '../../../services/traffic-fine-driver-indication.service';
import { firstValueFrom, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  CustomerSelection,
  CustomerSelectionModalComponent
} from '../../service-orders/customer-selection-modal/customer-selection-modal.component';

interface TrafficFineDetailField {
  label: string;
  keys: string[];
  type?: 'text' | 'date' | 'datetime' | 'currency' | 'boolean' | 'percent';
}

interface TrafficFineDetailSection {
  title: string;
  icon: string;
  fields: TrafficFineDetailField[];
}

@Component({
  selector: 'app-traffic-fines',
  templateUrl: './traffic-fines.component.html',
  styleUrls: ['./traffic-fines.component.css']
})
export class TrafficFinesComponent implements OnInit {
  tableTitle = 'Multas de Trânsito';
  showSearch = true;
  searchPlaceholder = 'Pesquisar multas...';
  clickable = true;

  columns = [
    { titulo: 'Nº AIT', label: 'aipNumber' },
    { titulo: 'Placa', label: 'vehiclePlate' },
    { titulo: 'Status Infração', label: 'infractionStatus' },
    { titulo: 'Data de Autuação', label: 'issueDate' },
    { titulo: 'Vencimento', label: 'dueDate' },
    { titulo: 'Valor', label: 'originalAmount' },
    { titulo: 'Status Pgto', label: 'paymentStatus' },
    { titulo: 'Cliente', label: 'customerName' }
  ];

  actions = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view', visible: true },
    {
      label: 'Indicar Condutor',
      icon: 'fas fa-user-plus',
      action: 'indicateDriver',
      class: 'btn-drivers',
      visible: true
    },
    {
      label: 'Vincular Cliente',
      icon: 'fas fa-user-tag',
      action: 'linkCustomer',
      class: 'btn-customer',
      visible: true
    },
    {
      label: 'Alterar Status',
      icon: 'fas fa-exchange-alt',
      action: 'changeStatus',
      class: 'btn-status',
      visible: true
    }
  ];


  fines: any[] = [];
  aba: 'lista' | 'sem-vinculo' = 'lista';
  loading = false;
  error: string | null = null;
  // Status modal
  availableStatuses: string[] = [
    'Licenciamento',
    'Identificado',
    'Recurso',
    'Correio',
    'Assinado',
    'Não Assinado',
    'Indicação Direta',
    'Reembolsado'
  ];
  showStatusSelectionModal = false;
  statusModalLoading = false;
  statusModalError: string | null = null;
  statusModalSelectedFine: any | null = null;
  statusModalSelectedStatus: string | null = null;

  @ViewChild('customerLinkModal') customerLinkModal!: CustomerSelectionModalComponent;
  linkCustomerFine: any | null = null;
  linkCustomerLoading = false;

  currentPage = 1;
  pageSize = 50;
  totalItems = 0;
  totalPages = 0;

  /** Texto: nome do cliente ou somente números para ID */
  customerSearch = '';
  vehiclePlate = '';
  /** Valor mínimo (R$): exibe multas com valor original maior ou igual */
  minAmount: number | null = null;
  /** Data (calendário) da infração — compara com data da infração ou autuação */
  infractionDate = '';
  /** Número do AIT */
  aitNumber = '';
  /** Período (data de autuação) */
  periodStartDate = '';
  periodEndDate = '';

  // details moved to separate screen; no in-list detail sections

  showDriverModal = false;
  driverModalLoading = false;
  driverDocumentLoading = false;
  driverIndicationSaved = false;
  driverModalError: string | null = null;
  driverModalWarning: string | null = null;
  driverModalFine: any | null = null;
  existingIndicationId: number | null = null;

  driverForm: TrafficFineDriverIndicationForm = {
    name: '',
    cpf: '',
    rg: '',
    address: '',
    cep: '',
    city: '',
    state: '',
    cnhNumber: '',
    cnhRegister: '',
    cnhUf: ''
  };

  constructor(
    private trafficFinesService: TrafficFinesService,
    private accountsReceivableService: AccountsReceivableService,
    private router: Router,
    private errorService: ErrorService,
    private cdr: ChangeDetectorRef
    ,
    private authService: AuthService,
    private syncService: TrafficFineAccountsPayableSyncService,
    private driverIndicationDocumentService: TrafficFineDriverIndicationDocumentService,
    private driverIndicationService: TrafficFineDriverIndicationService
  ) {}

  ngOnInit(): void {
    this.applyFilters();
  }

  onSearch(_term: string): void {}

  search(): void {
    this.currentPage = 1;
    this.applyFilters();
  }

  applyFilters(): void {
    this.loading = true;
    this.error = null;

    const next = (res: any) => {
      const rawItems = res.items || [];
      this.fines = this.applyRowActions(rawItems.map((item: any) => this.normalizeListRow(item)));
      this.totalItems = res.totalItems ?? 0;
      this.totalPages = res.totalPages ?? 0;
      this.currentPage = res.page ?? this.currentPage;
      this.loading = false;
      this.syncListPaymentStatus();
    };

    const errorFn = (err: any) => {
      this.error = err?.message || 'Erro ao carregar multas';
      this.loading = false;
      this.fines = [];
    };

    this.trafficFinesService.getAll(this.buildListQuery()).subscribe({ next, error: errorFn });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.applyFilters();
  }

  onPageSizeChange(size: number): void {
    this.pageSize = size;
    this.currentPage = 1;
    this.applyFilters();
  }

  clearFilters(): void {
    this.customerSearch = '';
    this.vehiclePlate = '';
    this.minAmount = null;
    this.infractionDate = '';
    this.aitNumber = '';
    this.periodStartDate = '';
    this.periodEndDate = '';
    this.currentPage = 1;
    this.applyFilters();
  }

  /** Monta query GET /trafficfines com paginação e filtros no backend. */
  private buildListQuery(): TrafficFineListQuery {
    const q: TrafficFineListQuery = {
      page: this.currentPage,
      pageSize: this.pageSize
    };
    const customer = this.customerSearch.trim();
    if (customer) {
      q.cliente = customer;
    }
    const plate = this.normalizePlate(this.vehiclePlate);
    if (plate) {
      q.placa = plate;
    }
    if (this.minAmount != null && !Number.isNaN(this.minAmount) && this.minAmount > 0) {
      q.maiorValor = this.minAmount;
    }
    if (this.infractionDate?.trim()) {
      q.dataInfracao = this.infractionDate.trim();
    }
    const ait = this.aitNumber.trim();
    if (ait) {
      q.aipNumber = ait;
    }
    if (this.periodStartDate?.trim()) {
      q.periodoInicio = this.periodStartDate.trim();
    }
    if (this.periodEndDate?.trim()) {
      q.periodoFim = this.periodEndDate.trim();
    }
    return q;
  }

  private normalizeListRow(item: any): any {
    const normalized = this.trafficFinesService.normalizeFineItem(item);
    const plate = normalized.vehiclePlate || normalized.plateNumber || '';
    const customerName = normalized.customerName || '';
    const row = {
      ...normalized,
      vehiclePlate: plate,
      customerName: customerName || '—'
    } as any;
    row._actions = this.buildRowActionsForItem(row);
    return row;
  }

  private applyRowActions(rows: any[]): any[] {
    return rows.map((row: any) => {
      try {
        const status = (row?.infractionStatus ?? '').toString();
        const notIndicable = this.isInfractionNotIndicable(status);
        row._actions = (this.actions || []).map(a => ({ ...a }));
        for (const a of row._actions) {
          if ((a.action || '').toString().toLowerCase() === 'changestatus') {
            a.visible = !notIndicable;
          }
        }
      } catch {
        // ignore
      }
      return row;
    });
  }

  private syncListPaymentStatus(): void {
    if (!this.fines.some(fine => this.syncService.isSyncCandidate(fine))) {
      return;
    }

    const userName =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      'Sistema';

    this.syncService.syncPaymentStatusBatch(this.fines, userName).subscribe({
      next: syncedRows => {
        this.fines = this.applyRowActions(
          syncedRows.map(row => this.normalizeListRow(row))
        );
        this.cdr.detectChanges();
      }
    });
  }

  private isInfractionNotIndicable(status: string): boolean {
    if (!status) return false;
    try {
      const s = String(status).normalize('NFD').replace(/[ -\u036f]/g, '').toLowerCase().replace(/[_\s-]+/g, '');
      return s === 'nicnaoindicavel' || s === 'nicnaoindicável' || s.includes('NIC') && s.includes('indica');
    } catch {
      return false;
    }
  }

  /** Override actions per-row to disable changeStatus when not indicable */
  private buildRowActionsForItem(item: any): any[] {
    const base = Array.isArray((item as any)._actions) ? (item as any)._actions : this.actions;
    const cloned = base.map((a: any) => ({ ...a }));
    const notIndicable = this.isInfractionNotIndicable(item?.infractionStatus || item?.InfractionStatus || '');
    for (const a of cloned) {
      if ((a.action || '').toString().toLowerCase() === 'changestatus') {
        a.disabled = !!notIndicable;
        a.visible = true; // keep visible but disabled when needed
      }
    }
    return cloned;
  }

  private normalizePlate(p: string): string {
    return p.replace(/\s|-/g, '').toUpperCase();
  }

  onActionClick(actionData: { action: string; item: any }): void {
    switch (actionData.action) {
      case 'view':
        this.navigateToDetail(actionData.item);
        break;
      case 'indicateDriver':
        this.onIndicateDriver(actionData.item);
        break;
      case 'changeStatus':
        this.onChangeStatus(actionData.item);
        break;
      case 'linkCustomer':
        this.onLinkCustomer(actionData.item);
        break;
      default:
        break;
    }
  }

  onLinkCustomer(item: any): void {
    if (this.linkCustomerLoading) {
      return;
    }

    const fineId = item?.id ?? item?.Id;
    if (!fineId) {
      this.errorService.showErrorMessage('ID da multa não encontrado.');
      return;
    }

    this.linkCustomerFine = item;
    this.customerLinkModal.open();
  }

  onCustomerLinked(customer: CustomerSelection): void {
    if (this.linkCustomerLoading || !this.linkCustomerFine) {
      return;
    }

    const fineId = this.linkCustomerFine?.id ?? this.linkCustomerFine?.Id;
    if (!fineId) {
      this.errorService.showErrorMessage('ID da multa não encontrado.');
      this.linkCustomerFine = null;
      return;
    }

    this.linkCustomerLoading = true;

    const userName =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      'Sistema';

    const payload = {
      CustomerId: customer.id,
      CustomerName: customer.nome,
      InfractionStatus: 'Indicação Direta',
      UserName: userName
    };

    this.trafficFinesService.update(Number(fineId), payload).subscribe({
      next: () => {
        this.linkCustomerLoading = false;
        this.linkCustomerFine = null;
        this.errorService.showSuccessMessage('Cliente vinculado com sucesso.');
        this.applyFilters();
      },
      error: err => {
        this.linkCustomerLoading = false;
        this.linkCustomerFine = null;
        this.errorService.showErrorMessage(
          this.errorService.formatHttpErrorForDisplay(err) || 'Erro ao vincular cliente.'
        );
      }
    });
  }

  onChangeStatus(item: any): void {
    if (this.isInfractionNotIndicable(item?.infractionStatus || item?.InfractionStatus || '')) {
      this.errorService.showErrorMessage('Status "NIC Não Indicável" não permite alteração de status.');
      return;
    }
    this.statusModalSelectedFine = item;
    this.statusModalSelectedStatus = item?.infractionStatus || item?.InfractionStatus || null;
    this.statusModalError = null;
    this.showStatusSelectionModal = true;
    this.cdr.detectChanges();
  }

  closeStatusModal(): void {
    this.showStatusSelectionModal = false;
    this.statusModalLoading = false;
    this.statusModalError = null;
    this.statusModalSelectedFine = null;
    this.statusModalSelectedStatus = null;
  }

  selectStatusFromModal(status: string): void {
    if (!this.statusModalSelectedFine) {
      this.statusModalError = 'Não foi possível identificar a multa.';
      return;
    }
    const id = this.statusModalSelectedFine?.id ?? this.statusModalSelectedFine?.Id;
    if (!id) {
      this.statusModalError = 'ID da multa não encontrado.';
      return;
    }

    this.statusModalLoading = true;
    this.statusModalError = null;

    const userName = this.authService.getUserNameFromToken() || this.authService.currentUserValue?.username || 'Sistema';
    // Use single canonical property name expected by backend to avoid duplicate column updates
    const payload: any = { InfractionStatus: status, UserName: userName };
    this.trafficFinesService.update(Number(id), payload).subscribe({
      next: () => {
        this.statusModalLoading = false;
        this.showStatusSelectionModal = false;
        this.statusModalSelectedFine = null;
        this.errorService.showSuccessMessage('Status alterado com sucesso.');
        this.applyFilters();
      },
      error: (err) => {
        this.statusModalLoading = false;
        this.statusModalError = this.errorService.formatHttpErrorForDisplay(err) || 'Erro ao alterar status.';
      }
    });
  }


  navigateToDetail(item: any): void {
    const id = item?.id ?? item?.Id;
    if (!id) {
      this.errorService.showErrorMessage('ID da multa não encontrado.');
      return;
    }
    this.router.navigate([`/financial/traffic-fines/${id}/view`], {
      state: { listRow: this.normalizeListRow(item) }
    });
  }

  // details modal removed; navigation to separate detail screen implemented

  onIndicateDriver(item: any): void {
    const fineId = item?.id ?? item?.Id;
    if (!fineId) {
      this.errorService.showErrorMessage('ID da multa não encontrado.');
      return;
    }

    this.driverModalError = null;
    this.driverModalWarning = null;
    this.driverDocumentLoading = false;
    this.driverIndicationSaved = false;
    this.existingIndicationId = null;
    this.driverModalFine = item;
    this.driverForm = this.emptyDriverForm();
    this.showDriverModal = true;
    this.driverModalLoading = true;
    this.cdr.detectChanges();

    this.trafficFinesService.getById(Number(fineId), item).pipe(
      switchMap(fine => {
        this.driverModalFine = fine;
        const resolvedIndicationId = this.resolveDriverIndicationId(fine, item);
        const numericFineId = Number(fineId);

        if (resolvedIndicationId) {
          return this.driverIndicationService.tryGetById(resolvedIndicationId).pipe(
            map(indication => ({ fine, indication, resolvedIndicationId }))
          );
        }

        return this.driverIndicationService.tryGetByTrafficFineId(numericFineId).pipe(
          map(indication => ({
            fine,
            indication,
            resolvedIndicationId: indication ? (this.getNumericId(indication) ?? null) : null
          }))
        );
      }),
      catchError(err => {
        this.driverModalError =
          this.errorService.formatHttpErrorForDisplay(err) || 'Erro ao carregar dados da multa.';
        return of(null);
      })
    ).subscribe(result => {
      if (result) {
        const { fine, indication, resolvedIndicationId } = result;
        if (resolvedIndicationId && !indication) {
          this.driverModalWarning =
            'Vínculo de indicação encontrado, mas o registro não está mais disponível. Preencha os dados novamente.';
          this.existingIndicationId = null;
        } else {
          this.driverModalWarning = null;
          this.existingIndicationId = indication
            ? (this.getNumericId(indication) ?? resolvedIndicationId)
            : null;
        }
        this.driverForm = this.buildDriverForm(fine, indication);
        this.driverIndicationSaved = !!(this.driverForm.name?.trim() && this.driverForm.cpf?.trim());
      }
      this.driverModalLoading = false;
      this.cdr.detectChanges();
    });
  }

  onDriverBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeDriverModal();
    }
  }

  closeDriverModal(): void {
    this.showDriverModal = false;
    this.driverModalLoading = false;
    this.driverDocumentLoading = false;
    this.driverIndicationSaved = false;
    this.driverModalError = null;
    this.driverModalWarning = null;
    this.driverModalFine = null;
    this.existingIndicationId = null;
  }

  saveDriverIndication(): void {
    if (!this.driverModalFine) {
      this.driverModalError = 'Não foi possível identificar a multa para indicar o condutor.';
      return;
    }

    const id = this.driverModalFine?.id ?? this.driverModalFine?.Id;
    if (!id) {
      this.driverModalError = 'ID da multa não encontrado.';
      return;
    }

    this.driverModalLoading = true;
    this.driverModalError = null;

    const userName =
      this.authService.getUserNameFromToken() ||
      this.authService.currentUserValue?.username ||
      'Sistema';

    const fineId = Number(id);
    const indicationPayload: TrafficFineDriverIndication = {
      driverName: this.driverForm.name,
      documentNumberCPF: this.driverForm.cpf,
      documentNumberRG: this.driverForm.rg,
      address: this.driverForm.address,
      cep: this.driverForm.cep,
      city: this.driverForm.city,
      state: this.driverForm.state,
      trafficFineId: fineId,
      userName
    };

    const indicationSave$ = this.existingIndicationId
      ? this.driverIndicationService.update(this.existingIndicationId, indicationPayload)
      : this.driverIndicationService.create(indicationPayload);

    const wasUpdate = !!this.existingIndicationId;

    indicationSave$
      .pipe(
        switchMap(saved => {
          const indicationId = this.getNumericId(saved) ?? this.existingIndicationId;
          const finePayload: Record<string, unknown> = {
            userName,
            driverName: this.driverForm.name,
            driverCpf: this.driverForm.cpf,
            driverRg: this.driverForm.rg,
            driverAddress: this.driverForm.address,
            driverCep: this.driverForm.cep,
            driverCity: this.driverForm.city,
            driverState: this.driverForm.state,
            driverCnhNumber: this.driverForm.cnhNumber,
            driverCnhRegister: this.driverForm.cnhRegister,
            driverCnhUf: this.driverForm.cnhUf,
            ...(indicationId ? { driverIndicationId: indicationId } : {})
          };
          return this.trafficFinesService.update(fineId, finePayload).pipe(map(() => indicationId));
        })
      )
      .subscribe({
        next: indicationId => {
          this.driverModalLoading = false;
          this.driverIndicationSaved = true;
          if (indicationId) {
            this.existingIndicationId = indicationId;
          }
          this.errorService.showSuccessMessage(
            wasUpdate ? 'Indicação atualizada com sucesso.' : 'Condutor indicado com sucesso.'
          );
          this.applyFilters();
          void this.generateDriverIndicationDocument(true);
        },
        error: err => {
          this.driverModalLoading = false;
          this.driverModalError =
            this.errorService.formatHttpErrorForDisplay(err) || 'Erro ao indicar condutor.';
        }
      });
  }

  async generateDriverIndicationDocument(autoClose = false): Promise<void> {
    if (!this.driverIndicationSaved) {
      this.driverModalError = 'Salve a indicação do condutor antes de gerar o documento.';
      return;
    }

    if (!this.driverModalFine) {
      this.driverModalError = 'Não foi possível identificar a multa para gerar o documento.';
      return;
    }

    const id = this.driverModalFine?.id ?? this.driverModalFine?.Id;
    if (!id) {
      this.driverModalError = 'ID da multa não encontrado.';
      return;
    }

    this.driverDocumentLoading = true;
    this.driverModalError = null;
    this.cdr.detectChanges();

    try {
      const fine = await firstValueFrom(
        this.trafficFinesService.getById(Number(id), this.driverModalFine)
      );
      await this.driverIndicationDocumentService.downloadDocument(fine, this.driverForm);
      this.errorService.showSuccessMessage('Documento de indicação gerado com sucesso.');
      if (autoClose) {
        this.closeDriverModal();
      }
    } catch (err: any) {
      this.driverModalError =
        err?.message || 'Erro ao gerar o documento de indicação do condutor.';
    } finally {
      this.driverDocumentLoading = false;
      this.cdr.detectChanges();
    }
  }

  private emptyDriverForm(): TrafficFineDriverIndicationForm {
    return {
      name: '',
      cpf: '',
      rg: '',
      address: '',
      cep: '',
      city: '',
      state: '',
      cnhNumber: '',
      cnhRegister: '',
      cnhUf: ''
    };
  }

  private resolveDriverIndicationId(...sources: any[]): number | null {
    for (const source of sources) {
      const id = this.getDriverIndicationId(source);
      if (id) {
        return id;
      }
    }
    return null;
  }

  private getDriverIndicationId(fine: any): number | null {
    const raw =
      fine?.driverIndicationId ??
      fine?.DriverIndicationId ??
      fine?.trafficFineDriverIndicationId ??
      fine?.TrafficFineDriverIndicationId;
    if (raw == null || raw === '') {
      return null;
    }
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  private buildDriverForm(
    fine: any,
    indication?: TrafficFineDriverIndication | null
  ): TrafficFineDriverIndicationForm {
    const pickInd = (keys: string[]): string => {
      if (!indication) {
        return '';
      }
      for (const key of keys) {
        const value = indication[key];
        if (value != null && String(value).trim() !== '') {
          return String(value).trim();
        }
      }
      return '';
    };

    return {
      name:
        pickInd(['driverName', 'DriverName']) ||
        this.getItemValue(fine, ['driverName', 'DriverName']),
      cpf:
        pickInd(['documentNumberCPF', 'DocumentNumberCPF']) ||
        this.getItemValue(fine, [
          'documentNumberCPF',
          'DocumentNumberCPF',
          'cpf',
          'CPF',
          'driverCpf',
          'DriverCpf'
        ]),
      rg:
        pickInd(['documentNumberRG', 'DocumentNumberRG']) ||
        this.getItemValue(fine, ['documentNumberRG', 'DocumentNumberRG', 'rg', 'RG', 'driverRg', 'DriverRg']),
      address:
        pickInd(['address', 'Address']) ||
        this.getItemValue(fine, ['address', 'Address', 'driverAddress', 'DriverAddress']),
      cep:
        pickInd(['cep', 'CEP']) ||
        this.getItemValue(fine, ['cep', 'CEP', 'driverCep', 'DriverCep']),
      city:
        pickInd(['city', 'City']) ||
        this.getItemValue(fine, ['city', 'City', 'driverCity', 'DriverCity']),
      state:
        pickInd(['state', 'State']) ||
        this.getItemValue(fine, ['state', 'State', 'driverState', 'DriverState']),
      cnhNumber: this.getItemValue(fine, ['cnhNumber', 'CnhNumber', 'driverCnhNumber', 'DriverCnhNumber']),
      cnhRegister: this.getItemValue(fine, ['cnhRegister', 'CnhRegister', 'driverCnhRegister', 'DriverCnhRegister']),
      cnhUf: this.getItemValue(fine, ['cnhUf', 'CnhUf', 'driverCnhUf', 'DriverCnhUf'])
    };
  }

  private getItemValue(item: any, keys: string[]): string {
    for (const key of keys) {
      const value = item?.[key];
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        return String(value);
      }
    }
    return '';
  }

  /** ID numérico da entidade (API pode retornar `id` ou `Id`). */
  private getNumericId(entity: any): number | null {
    const id = entity?.id ?? entity?.Id;
    if (id == null || id === '') {
      return null;
    }
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  onFinancial(item: any): void {
    // removed: financial modal handled in detail view now
  }
 
  goToDashboard(): void {
    this.router.navigate(['/financial/traffic-fines/dashboard']);
  }

  exportToCSV(): void {
    const cols = this.columns;
    const header = cols.map(c => `"${c.titulo}"`).join(',');
    const rows = this.fines.map(item =>
      cols.map(c => `"${String((item as any)[c.label] ?? '').replace(/"/g, '""')}"`).join(',')
    );
    const csv = '﻿' + [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'multas.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  formatStatus(value: string): string {
    return value ? value.replace(/_/g, ' ') : '';
  }
  

  private formatCurrency(value: any): string {
    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) {
      return String(value);
    }

    return numericValue.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }

  private formatDate(value: any): string {
    const date = this.parseDate(value);
    return date ? date.toLocaleDateString('pt-BR') : String(value);
  }

  private formatDateTime(value: any): string {
    const date = this.parseDate(value);
    return date ? date.toLocaleString('pt-BR') : String(value);
  }

  private parseDate(value: any): Date | null {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
}
